/**
 * Phase 20.4 — Beat of the Week Hero
 * Surfaces the canonical BOTW pick on top of /beats and (optionally) on /shuffle.
 * One trophy-styled card with producer attribution + Play (drives global player).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { usePlayer } from "@/components/player/PlayerProvider";
import { getProfilePhotoUrl, optimizeImage } from "@/lib/mediaUrls";
import GiftPicker from "@/components/GiftPicker";

const BeatOfTheWeekHero = ({ currentUserId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const player = usePlayer();

  useEffect(() => {
    let alive = true;
    api.get("/beat-of-the-week/current")
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  if (loading || !data?.beat) return null;
  const b = data.beat;
  const trackId = `beat_${b.beat_id}`;
  const isCurrent = player?.track?.trackId === trackId;
  const playing = !!isCurrent && player?.isPlaying;

  const handlePlay = () => {
    if (!b.audio_url) return;
    if (isCurrent) { player.toggle(); return; }
    player.play({
      trackId, beat_id: b.beat_id, title: b.title,
      artist_name: b.producer?.name || "Producer",
      artist_id: b.producer?.user_id || b.user_id,
      cover_url: b.cover_url, audio_url: b.audio_url, kind: "beat",
    }, { sourceLabel: "🏆 Beat of the Week" });
  };

  const isOwner = !!currentUserId && b.user_id === currentUserId;

  return (
    <Card
      className="relative overflow-hidden mb-6 bg-gradient-to-br from-amber-900/30 via-purple-900/20 to-pink-900/30 border-2 border-amber-400/40"
      data-testid="botw-hero"
    >
      <div className="absolute inset-0 opacity-30 pointer-events-none">
        <div className="absolute top-0 left-0 w-64 h-64 bg-amber-500/30 blur-3xl rounded-full" />
        <div className="absolute bottom-0 right-0 w-64 h-64 bg-pink-500/30 blur-3xl rounded-full" />
      </div>
      <div className="relative p-5 flex flex-col md:flex-row gap-5 items-stretch">
        {/* Cover */}
        <Link to={`/beats/${b.beat_id}`}
              className="block w-full md:w-44 lg:w-52 aspect-square rounded-xl overflow-hidden bg-black/40 ring-2 ring-amber-300/40 flex-shrink-0 group"
              data-testid="botw-hero-cover">
          {b.cover_url && (
            <img src={optimizeImage(b.cover_url, 'card')} alt={b.title}
                 className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
          )}
        </Link>
        {/* Meta */}
        <div className="flex-1 min-w-0 flex flex-col justify-between gap-3">
          <div>
            <p className="text-[11px] uppercase tracking-widest text-amber-300 font-black">
              🏆 Beat of the Week · {data.week_id}
            </p>
            <Link to={`/beats/${b.beat_id}`} data-testid="botw-hero-title">
              <h2 className="text-2xl sm:text-3xl font-black text-white mt-1 hover:text-amber-200 transition-colors">
                {b.title}
              </h2>
            </Link>
            <Link to={`/artist/${b.producer?.user_id}`}
                  className="inline-flex items-center gap-2 mt-2 text-sm text-purple-200 hover:text-amber-200"
                  data-testid="botw-hero-producer">
              <img
                src={getProfilePhotoUrl({ profile_photo: b.producer?.profile_photo, picture: b.producer?.profile_photo })}
                alt=""
                className="w-6 h-6 rounded-full border border-amber-400/30 object-cover"
              />
              <span>by {b.producer?.name || "Producer"}</span>
            </Link>
            <div className="flex gap-3 mt-2 text-[11px] text-amber-200/70">
              <span>▶ {(b.plays || 0).toLocaleString()} plays</span>
              {(b.gifts_received || 0) > 0 && <span>🎁 {b.gifts_received}</span>}
              {(b.repost_count || 0) > 0 && <span>🔁 {b.repost_count}</span>}
              {b.bpm && <span>{b.bpm} BPM</span>}
              {b.genre && <span>{b.genre}</span>}
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              onClick={handlePlay}
              className="bg-gradient-to-r from-amber-500 to-pink-600 text-black font-black hover:opacity-90"
              data-testid="botw-hero-play"
            >
              {playing ? "❚❚ Pause" : "▶ Play Beat of the Week"}
            </Button>
            <Link to={`/beats/${b.beat_id}`}>
              <Button variant="outline" className="border-amber-400/40 text-white"
                      data-testid="botw-hero-open">
                Open Beat
              </Button>
            </Link>
            {!isOwner && currentUserId && (
              <GiftPicker
                targetType="beat"
                targetId={b.beat_id}
                recipientId={b.user_id}
                currentUserId={currentUserId}
                compact
                className="shrink-0"
              />
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

export default BeatOfTheWeekHero;
